import type {BaseFetchResultsInterface} from '@interfaces/base.ts'
import type {ConnectionDeviceEdgeApiInterface} from '@app/shared/interfaces/edgeapi/connectionEgeDeviceApi.ts'
import type {ConnectionEdgeApiInterface} from '@app/shared/interfaces/edgeapi/connectionEdgeAPI.ts'
import type {ConnectionNativeEdgeApiInterface} from '@app/shared/interfaces/edgeapi/connectionEdgeNativeApi'
import {ConnectionTypeEdgeApiEnum} from '@app/shared/interfaces/edgeapi/connectionEdgeAPI.ts'
import type {
  BulkEnableDataPointsResponse,
  DataPointEdgeApiInterface,
  DataPointGetEdgeApiInterface,
  DataPointUploadResponse,
} from '@interfaces/edgeapi/dataPointEdgeApi'
import type {DataSourceEdgeApiInterface} from '@interfaces/edgeapi/dataSourceEdgeApi'
import type {EndpointEdgeApiInterface} from '@interfaces/edgeapi/endpointEdgeApi'
import type {PaginatedResponse} from '@interfaces/common/paginatedResponse.ts'
import type {
  LatestEdgeNativeReleaseInterface,
  ReleaseAssetDownloadLinkInterface,
  ReleasesEdgeNativeRowInterface,
  ReleasesIgnitionRowInterface,
} from '@app/shared/interfaces/edgeapi/releasesEdgeNative'
import {createApi} from '@reduxjs/toolkit/query/react'
import {createMontBlancBaseQuery} from '@apis/helpers.ts'

// A connection is tagged by its resourcetype; a data source mutation only knows the id.
const connectionTags = (connectionId: string) =>
  [
    {type: ConnectionTypeEdgeApiEnum.CONNECTION_DEVICE, id: connectionId},
    {type: ConnectionTypeEdgeApiEnum.CONNECTION_NATIVE, id: connectionId},
  ] as const

export const edgeApi = createApi({
  reducerPath: 'edgeApi',
  baseQuery: createMontBlancBaseQuery('edge'),
  tagTypes: [
    'Connection',
    'ConnectionDevice',
    'ConnectionNative',
    'ConnectionMQTT',
    'DataSource',
    'DataPoint',
    'Endpoint',
    'ReleasesEdgeNative',
    'ReleasesIgnition',
  ],

  endpoints: (builder) => ({
    // ** Connections -- Create
    createConnectionEdgeAPI: builder.mutation<
      ConnectionEdgeApiInterface,
      {body: Omit<ConnectionEdgeApiInterface, 'id'>}
    >({
      query: ({body}) => ({
        url: 'connections/',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result) => (result ? [result.resourcetype] : ['Connection']),
    }),

    // ** Connections -- List
    listConnectionsEdgeAPI: builder.query<
      PaginatedResponse<ConnectionEdgeApiInterface>,
      {type?: string; limit?: number; offset?: number; ordering?: string; search?: string}
    >({
      query: ({type, limit, offset, ordering, search} = {}) => ({
        url: 'connections/',
        params: {
          resourcetype: type,
          limit,
          offset,
          ordering,
          search: search !== null ? search : undefined,
        },
      }),
      transformResponse: (response: PaginatedResponse<ConnectionEdgeApiInterface>) => response,
      providesTags: (result) =>
        result?.results
          ? [
              ...result.results.map(({id, resourcetype}) => ({type: resourcetype, id})),
              {type: 'Connection', id: 'LIST'},
            ]
          : [{type: 'Connection', id: 'LIST'}],
    }),
    // ** Connection Counts -- Get
    getConnectionCountsEdgeAPI: builder.query<
      {edge_device: number; edge_native: number; mqtt: number},
      void
    >({
      query: () => ({
        url: 'connections/counts/',
      }),
      providesTags: [
        {type: ConnectionTypeEdgeApiEnum.CONNECTION_DEVICE, id: 'COUNT'},
        {type: ConnectionTypeEdgeApiEnum.CONNECTION_NATIVE, id: 'COUNT'},
        {type: ConnectionTypeEdgeApiEnum.CONNECTION_MQTT, id: 'COUNT'},
      ],
    }),

    // ** Connections -- Get
    getConnectionEdgeAPI: builder.query<ConnectionEdgeApiInterface, {connectionId: string}>({
      query: ({connectionId}) => `connections/${connectionId}/`,
      providesTags: (result, error) => {
        if (error) {
          console.error('Error fetching Connection by ID:', error)
        }

        return result ? [{type: result.resourcetype, id: result.id}] : []
      },
    }),

    getConnectionNameEdgeAPI: builder.query<{name: string}, {connectionId: string}>({
      query: ({connectionId}) => `connections/${connectionId}/name/`,
      providesTags: (result, error, {connectionId}) => {
        if (error) {
          console.error('Error fetching Connection name:', error)
        }
        return result ? [{type: 'Connection', id: connectionId}] : []
      },
    }),

    getConnectionByIdentifierEdgeAPI: builder.query<
      ConnectionEdgeApiInterface,
      {identifier: string}
    >({
      query: ({identifier}) => `connections/identifier-${identifier}/`,
      transformResponse: (
        response:
          | ConnectionEdgeApiInterface
          | BaseFetchResultsInterface<ConnectionEdgeApiInterface>,
      ) => response as ConnectionEdgeApiInterface,
      providesTags: (result) => (result ? [{type: result.resourcetype, id: result.id}] : []),
    }),

    // ** Connections -- Update
    updateConnectionEdgeAPI: builder.mutation<
      ConnectionDeviceEdgeApiInterface,
      {connectionId: string; body: Partial<ConnectionDeviceEdgeApiInterface>}
    >({
      query: ({connectionId, body}) => ({
        url: `connections/${connectionId}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result) => (result ? [result.resourcetype] : ['Connection']),
    }),

    updateConnectionByIdentifierEdgeAPI: builder.mutation<
      ConnectionEdgeApiInterface,
      {identifier: string; resourcetype: string; body: Partial<ConnectionDeviceEdgeApiInterface>}
    >({
      query: ({identifier, body}) => ({
        url: `connections/identifier-${identifier}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result) => (result ? [result.resourcetype] : ['Connection']),
    }),

    // ** Connections -- Delete
    deleteConnectionEdgeAPI: builder.mutation<
      {message: string},
      {connectionId: string; resourcetype: ConnectionTypeEdgeApiEnum}
    >({
      query: ({connectionId}) => ({
        url: `connections/${connectionId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, {resourcetype}) =>
        resourcetype ? [resourcetype] : ['Connection'],
    }),

    // ** Connection -- Reprovision (AB#6213): rotates the Native connection's credentials and
    // returns a new install token. No invalidation: nothing listed changes, and a refetch must not
    // re-render the cell that shows the one-time install command.
    reprovisionConnectionEdgeAPI: builder.mutation<
      ConnectionNativeEdgeApiInterface,
      {connectionId: string}
    >({
      query: ({connectionId}) => ({
        url: `connections/${connectionId}/reprovision/`,
        method: 'POST',
      }),
    }),

    // ** Data Sources -- Create
    createDataSourceEdgeAPI: builder.mutation<
      DataSourceEdgeApiInterface,
      {connectionId: string; body: Omit<DataSourceEdgeApiInterface, 'id'>}
    >({
      query: ({connectionId, body}) => ({
        url: `/connections/${connectionId}/data_sources/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['DataSource'],
    }),

    // ** Data Sources -- List
    listDataSourcesEdgeAPI: builder.query<
      PaginatedResponse<DataSourceEdgeApiInterface>,
      {
        connectionId: string
        connectionType?: string
        limit?: number
        offset?: number
        ordering?: string
        search?: string
      }
    >({
      query: ({connectionId, limit = 10, offset = 0, ordering, search}) => ({
        url: `connections/${connectionId}/data_sources/`,
        params: {
          limit,
          offset,
          ordering,
          search: search !== null ? search : undefined,
        },
      }),
      transformResponse: (response: PaginatedResponse<DataSourceEdgeApiInterface>) => response,
      providesTags: (result, error, {connectionId}) =>
        result?.results
          ? [
              ...result.results.map(({id}) => ({type: 'DataSource' as const, id})),
              {
                type: 'DataSource',
                id: 'LIST',
                connectionId,
              },
              {
                type: 'DataPoint',
                id: 'LIST',
              },
            ]
          : [
              {
                type: 'DataSource',
                id: 'LIST',
                connectionId,
              },
              {
                type: 'DataPoint',
                id: 'LIST',
              },
            ],
    }),

    // ** Data Sources -- Get
    getDataSourceEdgeAPI: builder.query<
      DataSourceEdgeApiInterface,
      {connectionId: string; dataSourceId: string}
    >({
      query: ({connectionId, dataSourceId}) =>
        `connections/${connectionId}/data_sources/${dataSourceId}/`,
      providesTags: (result, error, {dataSourceId}) =>
        result ? [{type: 'DataSource', id: dataSourceId}] : [],
    }),

    // ** Data Sources -- Update
    updateDataSourceEdgeAPI: builder.mutation<
      {url: string; message?: string},
      {connectionId: string; dataSourceId: string; body: Partial<DataSourceEdgeApiInterface>}
    >({
      query: ({connectionId, dataSourceId, body}) => ({
        url: `connections/${connectionId}/data_sources/${dataSourceId}/`,
        method: 'PATCH',
        body,
      }),
      // The connection's credentials_reentry_required (AB#6429) can clear with this save.
      invalidatesTags: (result, error, {connectionId}) => [
        'DataSource',
        ...connectionTags(connectionId),
      ],
    }),

    // ** Data Sources -- Delete
    deleteDataSourceEdgeAPI: builder.mutation<
      {message: string},
      {connectionId: string; dataSourceId: string}
    >({
      query: ({connectionId, dataSourceId}) => ({
        url: `connections/${connectionId}/data_sources/${dataSourceId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, {connectionId}) => [
        'DataSource',
        ...connectionTags(connectionId),
      ],
    }),

    // ** Data Points -- Create
    createDataPointEdgeAPI: builder.mutation<
      DataPointEdgeApiInterface,
      {connectionId: string; dataSourceId: string; body: Omit<DataPointEdgeApiInterface, 'id'>}
    >({
      query: ({connectionId, dataSourceId, body}) => ({
        url: `/connections/${connectionId}/data_sources/${dataSourceId}/data_points/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['DataPoint'],
    }),

    // ** Data Points -- List
    listDataPointEdgeAPI: builder.query<
      PaginatedResponse<DataPointGetEdgeApiInterface>,
      {
        connectionId: string
        dataSourceId: string
        limit?: number
        offset?: number
        ordering?: string
        search?: string
        is_enabled?: boolean
        data_type?: string
        health_status?: string
        health_status__in?: string
      }
    >({
      query: ({
        connectionId,
        dataSourceId,
        limit = 5000,
        offset = 0,
        ordering,
        search,
        is_enabled,
        data_type,
        health_status,
        health_status__in,
      }) => ({
        url: `connections/${connectionId}/data_sources/${dataSourceId}/data_points/`,
        params: {
          limit,
          offset,
          ordering,
          search: search !== null ? search : undefined,
          is_enabled,
          data_type,
          health_status,
          health_status__in,
        },
      }),
      transformResponse: (response: BaseFetchResultsInterface<DataPointGetEdgeApiInterface>) =>
        response,
      providesTags: (result, error, {dataSourceId}) =>
        result?.results
          ? [
              ...result.results.map(({id}) => ({type: 'DataPoint' as const, id})),
              {
                type: 'DataPoint',
                id: 'LIST',
                dataSourceId,
              },
            ]
          : [
              {
                type: 'DataPoint',
                id: 'LIST',
                dataSourceId,
              },
            ],
    }),

    // ** Data Point -- Update
    updateDataPointEdgeAPI: builder.mutation<
      {url: string; message?: string},
      {
        connectionId: string
        dataSourceId: string
        dataPointId: string
        body: Partial<DataPointEdgeApiInterface>
      }
    >({
      query: ({connectionId, dataSourceId, dataPointId, body}) => ({
        url: `connections/${connectionId}/data_sources/${dataSourceId}/data_points/${dataPointId}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: () => ['DataPoint'],
    }),

    // ** Data Points -- Bulk Enable/Disable
    updateDataPointBulkEnabledEdgeAPI: builder.mutation<
      BulkEnableDataPointsResponse,
      {connectionId: string; dataSourceId: string; dataPoints: string[]; isEnabled: boolean}
    >({
      query: ({connectionId, dataSourceId, dataPoints, isEnabled}) => ({
        url: `connections/${connectionId}/data_sources/${dataSourceId}/data_points:bulk_enable/`,
        method: 'POST',
        body: {
          ids: dataPoints,
          is_enabled: isEnabled,
        },
      }),
      invalidatesTags: ['DataPoint'],
    }),

    // ** Data Points -- Delete
    deleteDataPointEdgeAPI: builder.mutation<
      {message: string},
      {connectionId: string; dataSourceId: string; dataPointId: string}
    >({
      query: ({connectionId, dataSourceId, dataPointId}) => ({
        url: `connections/${connectionId}/data_sources/${dataSourceId}/data_points/${dataPointId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['DataPoint'],
    }),

    // ** Data Points -- Bulk Delete
    deleteDataPointBulkEdgeAPI: builder.mutation<
      {message: string},
      {connectionId: string; dataSourceId: string; dataPoints: string[]}
    >({
      query: ({connectionId, dataSourceId, dataPoints}) => ({
        url: `connections/${connectionId}/data_sources/${dataSourceId}/data_points:bulk_delete/`,
        method: 'POST',
        body: {
          ids: dataPoints,
        },
      }),
      invalidatesTags: ['DataPoint'],
    }),

    // ** Data Points -- Upload (CSV/Excel)
    uploadDataPointFileEdgeAPI: builder.mutation<
      DataPointUploadResponse,
      {connectionId: string; dataSourceId: string; file: FormData}
    >({
      query: ({connectionId, dataSourceId, file}) => ({
        url: `connections/${connectionId}/data_sources/${dataSourceId}/data_points:upload/`,
        method: 'POST',
        body: file,
      }),
      invalidatesTags: ['DataPoint'],
    }),

    // ** Endpoints -- List (AB#6006; generalized from "targets" in the
    // 2026-09-03 Origin/Endpoint redesign). What the automation editor's
    // http_call action picker offers -- never credentials, see
    // EndpointEdgeApiInterface.
    listEndpointsEdgeAPI: builder.query<
      PaginatedResponse<EndpointEdgeApiInterface>,
      {connection?: string; limit?: number; offset?: number; search?: string} | void
    >({
      query: ({connection, limit, offset, search} = {}) => ({
        url: 'endpoints/',
        // `search` matches the target id and the device the endpoint came
        // through, the two things the picker shows (AB#6720).
        params: {connection, limit, offset, search},
      }),
      providesTags: (result) =>
        result?.results
          ? [
              ...result.results.map(({uuid}) => ({type: 'Endpoint' as const, id: uuid})),
              {type: 'Endpoint' as const, id: 'LIST'},
            ]
          : [{type: 'Endpoint' as const, id: 'LIST'}],
    }),

    // ** Edge Native Releases -- List
    getEdgeNativeReleases: builder.query<
      PaginatedResponse<ReleasesEdgeNativeRowInterface>,
      {limit?: number; offset?: number; ordering?: string; search?: string}
    >({
      query: ({limit, offset, ordering, search}) => ({
        url: 'release-edge-native-assets/',
        params: {limit, offset, ordering, search: search !== null ? search : undefined},
      }),
      transformResponse: (response: PaginatedResponse<ReleasesEdgeNativeRowInterface>) => response,
      providesTags: (result) => (result ? [{type: 'ReleasesEdgeNative', id: 'LIST'}] : []),
    }),

    // ** Edge Native Releases -- Latest (AB#4219/5863: install command needs the real,
    // versioned filename + an authenticated download URL, not a placeholder to rename to)
    getLatestEdgeNativeRelease: builder.query<LatestEdgeNativeReleaseInterface, void>({
      query: () => ({url: 'release-edge-native/latest/'}),
      providesTags: [{type: 'ReleasesEdgeNative', id: 'LATEST'}],
    }),

    // ** Edge Native Releases -- 1 h signed download link on the API host (AB#6563)
    getEdgeNativeAssetDownloadLink: builder.query<ReleaseAssetDownloadLinkInterface, string>({
      query: (assetId) => ({url: `release-edge-native-assets/${assetId}/download-link/`}),
      keepUnusedDataFor: 0,
    }),

    // ** Ignition Releases -- List
    getIgnitionReleases: builder.query<
      PaginatedResponse<ReleasesIgnitionRowInterface>,
      {
        limit?: number
        offset?: number
        ordering?: string
        search?: string
        ignitionVersions?: Array<'8.1' | '8.3'>
      }
    >({
      query: ({limit, offset, ordering, search, ignitionVersions}) => ({
        url: 'release-ignition-assets/',
        params: {
          limit,
          offset,
          ordering,
          search: search !== null ? search : undefined,
          ignition_version__in: ignitionVersions?.length ? ignitionVersions.join(',') : undefined,
        },
      }),
      transformResponse: (response: PaginatedResponse<ReleasesIgnitionRowInterface>) => response,
      providesTags: (result) => (result ? [{type: 'ReleasesIgnition', id: 'LIST'}] : []),
    }),

    // ** Ignition Releases -- 1 h signed download link on the API host (AB#6563)
    getIgnitionAssetDownloadLink: builder.query<ReleaseAssetDownloadLinkInterface, string>({
      query: (assetId) => ({url: `release-ignition-assets/${assetId}/download-link/`}),
      keepUnusedDataFor: 0,
    }),
  }),
})

export const {
  // ** Connection -- Create
  useCreateConnectionEdgeAPIMutation,
  // ** Connection -- List
  useListConnectionsEdgeAPIQuery,
  // ** Connection -- Get
  useGetConnectionEdgeAPIQuery,
  useLazyGetConnectionByIdentifierEdgeAPIQuery,
  useGetConnectionNameEdgeAPIQuery,
  // ** Connection -- Counts
  useGetConnectionCountsEdgeAPIQuery,
  // ** Connection -- Update
  useUpdateConnectionEdgeAPIMutation,
  useUpdateConnectionByIdentifierEdgeAPIMutation,
  // ** Connection -- Delete
  useDeleteConnectionEdgeAPIMutation,
  // ** Connection -- Reprovision
  useReprovisionConnectionEdgeAPIMutation,

  // ** Data Source -- Create
  useCreateDataSourceEdgeAPIMutation,
  // ** Data Source -- List
  useListDataSourcesEdgeAPIQuery,
  // ** Data Source -- Get
  useGetDataSourceEdgeAPIQuery,
  // ** Data Source -- Update
  useUpdateDataSourceEdgeAPIMutation,
  // ** Data Source -- Delete
  useDeleteDataSourceEdgeAPIMutation,

  // ** Data Point -- Create
  useCreateDataPointEdgeAPIMutation,
  // ** Data Point -- List
  useListDataPointEdgeAPIQuery,
  useLazyListDataPointEdgeAPIQuery,
  // ** Data Point -- Update
  useUpdateDataPointEdgeAPIMutation,
  // ** Data Point -- Delete
  useDeleteDataPointEdgeAPIMutation,
  // ** Data Point -- Bulk Delete
  useDeleteDataPointBulkEdgeAPIMutation,
  useUpdateDataPointBulkEnabledEdgeAPIMutation,
  // ** Data Point -- Upload (CSV/Excel)
  useUploadDataPointFileEdgeAPIMutation,

  // ** Job Runner Targets -- List
  useListEndpointsEdgeAPIQuery,

  // ** Edge Native Releases
  useGetEdgeNativeReleasesQuery,
  useGetLatestEdgeNativeReleaseQuery,
  useLazyGetEdgeNativeAssetDownloadLinkQuery,
  // ** Ignition Releases
  useGetIgnitionReleasesQuery,
  useLazyGetIgnitionAssetDownloadLinkQuery,
} = edgeApi
